import React, { useState, useMemo } from 'react';
import {
  Mountain,
  Compass,
  Map,
  Navigation,
  TreePine,
  MapPin,
  Layers,
  Search,
  Shapes,
  Grid3X3
} from 'lucide-react';

export const MetaphorStudio: React.FC = () => {
  const [selectedMetaphor, setSelectedMetaphor] = useState<string>('all');
  const [selectedElement, setSelectedElement] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCommitted, setShowCommitted] = useState(true);
  const [showExperimental, setShowExperimental] = useState(true);

  // Core Brand Metaphors
  const brandMetaphors = [
    { 
      id: 'mountains', 
      name: 'Mountains', 
      icon: Mountain, 
      description: 'Peaks, elevation, achievement, challenge, summit', 
      category: 'terrain',
      isCommitted: true,
      elements: ['peak', 'summit', 'ridge', 'slope', 'base', 'elevation', 'ascent', 'descent']
    },
    { 
      id: 'navigation', 
      name: 'Navigation', 
      icon: Navigation, 
      description: 'Direction, guidance, course, route, journey', 
      category: 'movement',
      isCommitted: true,
      elements: ['course', 'bearing', 'heading', 'direction', 'path', 'route', 'waypoint']
    },
    { 
      id: 'wayfinding', 
      name: 'Wayfinding', 
      icon: Compass, 
      description: 'Orientation, discovery, exploration, finding your way', 
      category: 'movement',
      isCommitted: true,
      elements: ['compass', 'landmark', 'signpost', 'trail', 'marker', 'beacon', 'guide']
    },
    { 
      id: 'maps', 
      name: 'Maps', 
      icon: Map, 
      description: 'Representation, overview, planning, visualization', 
      category: 'representation',
      isCommitted: true,
      elements: ['chart', 'atlas', 'blueprint', 'layout', 'overview', 'terrain', 'topography']
    },
    { 
      id: 'outdoors', 
      name: 'The Outdoors', 
      icon: TreePine, 
      description: 'Nature, wilderness, adventure, exploration, natural elements', 
      category: 'environment',
      isCommitted: true,
      elements: ['forest', 'trail', 'wilderness', 'nature', 'adventure', 'exploration', 'elements']
    },
    { 
      id: 'compass', 
      name: 'Compass', 
      icon: Compass, 
      description: 'Direction, orientation, guidance, true north', 
      category: 'tool',
      isCommitted: true,
      elements: ['north', 'south', 'east', 'west', 'bearing', 'direction', 'orientation']
    },
    { 
      id: 'signage', 
      name: 'Signage', 
      icon: MapPin, 
      description: 'Markers, indicators, information, guidance', 
      category: 'communication',
      isCommitted: true,
      elements: ['sign', 'marker', 'indicator', 'label', 'tag', 'badge', 'symbol']
    },
    { 
      id: 'terrain', 
      name: 'Terrain', 
      icon: Layers, 
      description: 'Landscape, topography, surface, ground, foundation', 
      category: 'terrain',
      isCommitted: true,
      elements: ['landscape', 'topography', 'surface', 'ground', 'foundation', 'base', 'layer']
    },
    { 
      id: 'geometrics', 
      name: 'Geometrics', 
      icon: Shapes, 
      description: 'Shapes, patterns, structure, form, design', 
      category: 'structure',
      isCommitted: true,
      elements: ['shape', 'pattern', 'structure', 'form', 'design', 'geometry', 'symmetry']
    },
    { 
      id: 'geometrics-landscapes', 
      name: 'Geometrics over Landscapes', 
      icon: Grid3X3, 
      description: 'Structured patterns over natural terrain, organized wilderness', 
      category: 'hybrid',
      isCommitted: false,
      elements: ['grid', 'pattern', 'overlay', 'structure', 'organization', 'system', 'network']
    }
  ];

  // Visual Elements for Metaphors
  const visualElements = [
    // Mountain Elements
    { id: 'peak', name: 'Peak', symbol: '▲', description: 'Summit, highest point, achievement', category: 'mountain', metaphor: 'mountains', isCommitted: true },
    { id: 'ridge', name: 'Ridge', symbol: '⛰', description: 'Mountain spine, connecting peaks', category: 'mountain', metaphor: 'mountains', isCommitted: true },
    { id: 'slope', name: 'Slope', symbol: '⛰', description: 'Inclined surface, ascent/descent', category: 'mountain', metaphor: 'mountains', isCommitted: true },
    { id: 'base', name: 'Base', symbol: '▬', description: 'Foundation, starting point', category: 'mountain', metaphor: 'mountains', isCommitted: true },
    
    // Navigation Elements
    { id: 'arrow', name: 'Arrow', symbol: '→', description: 'Direction, movement, progress', category: 'navigation', metaphor: 'navigation', isCommitted: true },
    { id: 'waypoint', name: 'Waypoint', symbol: '●', description: 'Checkpoint, milestone, destination', category: 'navigation', metaphor: 'navigation', isCommitted: true },
    { id: 'route', name: 'Route', symbol: '━', description: 'Path, course, journey', category: 'navigation', metaphor: 'navigation', isCommitted: true },
    
    // Compass Elements
    { id: 'compass-rose', name: 'Compass Rose', symbol: '✧', description: 'Directional indicator, orientation', category: 'compass', metaphor: 'compass', isCommitted: true },
    { id: 'north', name: 'North', symbol: '↑', description: 'True north, primary direction', category: 'compass', metaphor: 'compass', isCommitted: true },
    
    // Map Elements
    { id: 'grid', name: 'Grid', symbol: '┼', description: 'Coordinate system, structure', category: 'map', metaphor: 'maps', isCommitted: true },
    { id: 'contour', name: 'Contour', symbol: '⌇', description: 'Elevation lines, terrain shape', category: 'map', metaphor: 'maps', isCommitted: true },
    { id: 'legend', name: 'Legend', symbol: '■', description: 'Key, explanation, reference', category: 'map', metaphor: 'maps', isCommitted: true },
    
    // Signage Elements
    { id: 'signpost', name: 'Signpost', symbol: '┴', description: 'Directional marker, information', category: 'signage', metaphor: 'signage', isCommitted: true },
    { id: 'trail-marker', name: 'Trail Marker', symbol: '◊', description: 'Path indicator, wayfinding', category: 'signage', metaphor: 'signage', isCommitted: true },
    
    // Terrain Elements
    { id: 'hill', name: 'Hill', symbol: '△', description: 'Elevated ground, gentle slope', category: 'terrain', metaphor: 'terrain', isCommitted: true },
    { id: 'valley', name: 'Valley', symbol: '⌄', description: 'Low area between elevations', category: 'terrain', metaphor: 'terrain', isCommitted: true },
    { id: 'plateau', name: 'Plateau', symbol: '▬', description: 'Flat elevated area, stability', category: 'terrain', metaphor: 'terrain', isCommitted: true },
    
    // Geometric Elements
    { id: 'triangle', name: 'Triangle', symbol: '△', description: 'Stability, hierarchy, direction', category: 'geometric', metaphor: 'geometrics', isCommitted: true },
    { id: 'circle', name: 'Circle', symbol: '○', description: 'Completeness, unity, cycle', category: 'geometric', metaphor: 'geometrics', isCommitted: true },
    { id: 'square', name: 'Square', symbol: '□', description: 'Foundation, stability, structure', category: 'geometric', metaphor: 'geometrics', isCommitted: true },
    { id: 'hexagon', name: 'Hexagon', symbol: '⬡', description: 'Efficiency, natural structure', category: 'geometric', metaphor: 'geometrics', isCommitted: true }
  ];

  // Color Palette for Metaphors
  const metaphorColors = [
    { id: 'mountain-blue', name: 'Mountain Sage', hex: '#5A7D5E', description: 'Peak shadows, depth, authority', category: 'mountain', isCommitted: true },
    { id: 'sage-green', name: 'Sage Green', hex: '#53675B', description: 'Forest, nature, growth', category: 'nature', isCommitted: true },
    { id: 'terracotta', name: 'Terracotta', hex: '#C97A5B', description: 'Earth, trails, warmth', category: 'earth', isCommitted: true },
    { id: 'ochre', name: 'Sunlit Ochre', hex: '#E8B86A', description: 'Light, energy, highlights', category: 'light', isCommitted: true },
    { id: 'sky-blue', name: 'Frosted Sky', hex: '#AFC6D9', description: 'Atmosphere, openness, clarity', category: 'sky', isCommitted: true },
    { id: 'ocean-teal', name: 'Deep Ocean Teal', hex: '#2F5C5B', description: 'Depth, navigation, calm', category: 'water', isCommitted: true }
  ];

  // Filtered elements based on selections
  const filteredElements = useMemo(() => {
    return visualElements.filter(element => {
      const matchesMetaphor = selectedMetaphor === 'all' || element.metaphor === selectedMetaphor;
      const matchesElement = selectedElement === 'all' || element.category === selectedElement;
      const matchesSearch = searchTerm === '' || 
        element.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        element.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCommitment = (showCommitted && element.isCommitted) || 
        (showExperimental && !element.isCommitted);
      
      return matchesMetaphor && matchesElement && matchesSearch && matchesCommitment;
    });
  }, [selectedMetaphor, selectedElement, searchTerm, showCommitted, showExperimental]);

  const filteredColors = useMemo(() => {
    return metaphorColors.filter(color => {
      const matchesMetaphor = selectedMetaphor === 'all' || color.category === selectedMetaphor;
      const matchesSearch = searchTerm === '' || 
        color.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        color.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCommitment = (showCommitted && color.isCommitted) || 
        (showExperimental && !color.isCommitted);
      
      return matchesMetaphor && matchesSearch && matchesCommitment;
    });
  }, [selectedMetaphor, searchTerm, showCommitted, showExperimental]);

  const getColorClasses = (color: string) => {
    const colors: Record<string, any> = {
      sage: {
        bg: 'bg-sage-50',
        border: 'border-sage-200',
        text: 'text-sage-900',
        accent: 'text-sage-600',
        hover: 'hover:bg-sage-100'
      },
      primary: {
        bg: 'bg-consulting-slate-blue-50',
        border: 'border-consulting-slate-blue-200',
        text: 'text-consulting-slate-blue-900',
        accent: 'text-consulting-slate-blue-600',
        hover: 'hover:bg-consulting-slate-blue-100'
      },
      amber: {
        bg: 'bg-[var(--mustard-dim)]',
        border: 'border-[var(--mustard)]',
        text: 'text-[var(--mustard)]',
        accent: 'text-[var(--mustard)]',
        hover: 'hover:bg-[var(--mustard-dim)]'
      },
      charcoal: {
        bg: 'bg-charcoal-50',
        border: 'border-charcoal-200',
        text: 'text-charcoal-900',
        accent: 'text-charcoal-600',
        hover: 'hover:bg-charcoal-100'
      }
    };
    return colors[color] || colors.primary;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <div className="h-16 w-16 bg-sage-500 flex items-center justify-center shadow-md">
            <Mountain className="h-10 w-10 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-charcoal-900">
              Metaphor Studio
            </h1>
            <p className="text-xl text-charcoal-600">
              Core Brand Metaphors & Visual Elements
            </p>
          </div>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <p className="text-lg text-charcoal-700 leading-relaxed">
            Experiment with the core metaphors that define the Peak Priorities brand: mountains, navigation, 
            wayfinding, maps, the outdoors, compass, signage, terrain, and geometrics over landscapes. 
            Build visual combinations that tell your story.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Metaphor Filter */}
          <div>
            <label className="block text-sm font-medium text-charcoal-700 mb-2">Metaphor</label>
            <select
              value={selectedMetaphor}
              onChange={(e) => setSelectedMetaphor(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
            >
              <option value="all">All Metaphors</option>
              {brandMetaphors.map(metaphor => (
                <option key={metaphor.id} value={metaphor.id}>{metaphor.name}</option>
              ))}
            </select>
          </div>

          {/* Element Category Filter */}
          <div>
            <label className="block text-sm font-medium text-charcoal-700 mb-2">Element Type</label>
            <select
              value={selectedElement}
              onChange={(e) => setSelectedElement(e.target.value)}
              className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
            >
              <option value="all">All Elements</option>
              <option value="mountain">Mountain</option>
              <option value="navigation">Navigation</option>
              <option value="compass">Compass</option>
              <option value="map">Map</option>
              <option value="signage">Signage</option>
              <option value="terrain">Terrain</option>
              <option value="geometric">Geometric</option>
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-charcoal-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search elements..."
                className="w-full pl-10 pr-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-500"
              />
            </div>
          </div>

          {/* Commitment Filter */}
          <div>
            <label className="block text-sm font-medium text-charcoal-700 mb-2">Status</label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showCommitted}
                  onChange={(e) => setShowCommitted(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Committed</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showExperimental}
                  onChange={(e) => setShowExperimental(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Experimental</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Brand Metaphors Overview */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Core Brand Metaphors</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {brandMetaphors.map(metaphor => {
            const Icon = metaphor.icon;
            const colors = getColorClasses(metaphor.isCommitted ? 'sage' : 'amber');
            return (
              <div key={metaphor.id} className={`p-4  border-2 ${colors.bg} ${colors.border}`}>
                <div className="flex items-center space-x-3 mb-3">
                  <Icon className={`h-6 w-6 ${colors.accent}`} />
                  <h3 className={`font-semibold ${colors.text}`}>{metaphor.name}</h3>
                </div>
                <p className="text-sm text-charcoal-600 mb-3">{metaphor.description}</p>
                <div className="flex flex-wrap gap-1">
                  {metaphor.elements.slice(0, 4).map(element => (
                    <span key={element} className="px-2 py-1 bg-[var(--bg-raised)] bg-opacity-50 text-xs rounded">
                      {element}
                    </span>
                  ))}
                  {metaphor.elements.length > 4 && (
                    <span className="px-2 py-1 bg-[var(--bg-raised)] bg-opacity-50 text-xs rounded">
                      +{metaphor.elements.length - 4}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Visual Elements */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">
          Visual Elements ({filteredElements.length})
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredElements.map(element => (
            <div key={element.id} className={`p-4  border-2 transition-all duration-200 hover:shadow-md ${
              element.isCommitted ? 'bg-sage-50 border-sage-200' : 'bg-[var(--mustard-dim)] border-[var(--mustard)]'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl">{element.symbol}</span>
                <span className={`text-xs px-2 py-1 rounded ${
                  element.isCommitted ? 'bg-sage-100 text-sage-800' : 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                }`}>
                  {element.isCommitted ? 'Committed' : 'Experimental'}
                </span>
              </div>
              <h3 className="font-semibold text-charcoal-900 mb-1">{element.name}</h3>
              <p className="text-sm text-charcoal-600 mb-2">{element.description}</p>
              <div className="text-xs text-charcoal-500">
                {brandMetaphors.find(m => m.id === element.metaphor)?.name} • {element.category}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Color Palette */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">
          Metaphor Color Palette ({filteredColors.length})
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredColors.map(color => (
            <div key={color.id} className="flex items-center space-x-4 p-4 border-2 border-[var(--rule)]">
              <div 
                className="w-12 h-12 border-2 border-[var(--rule)]"
                style={{ backgroundColor: color.hex }}
              ></div>
              <div className="flex-1">
                <h3 className="font-semibold text-charcoal-900">{color.name}</h3>
                <p className="text-sm text-charcoal-600 mb-1">{color.description}</p>
                <div className="flex items-center space-x-2">
                  <code className="text-xs bg-[var(--hover-tint)] px-2 py-1 rounded">{color.hex}</code>
                  <span className={`text-xs px-2 py-1 rounded ${
                    color.isCommitted ? 'bg-sage-100 text-sage-800' : 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                  }`}>
                    {color.isCommitted ? 'Committed' : 'Experimental'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Experimentation Area */}
      <div className="bg-gradient-to-r from-sage-50 to-amber-50 border-2 border-sage-200 p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Metaphor Experimentation</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-charcoal-800 mb-4">Combination Builder</h3>
            <p className="text-charcoal-600 mb-4">
              Select metaphors, elements, and colors to create visual combinations that tell your story.
            </p>
            <div className="space-y-3">
              <button className="w-full px-4 py-2 bg-sage-600 text-white hover:bg-sage-700 transition-colors">
                Start New Combination
              </button>
              <button className="w-full px-4 py-2 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] transition-colors">
                Load Template
              </button>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-charcoal-800 mb-4">Export Options</h3>
            <p className="text-charcoal-600 mb-4">
              Export your metaphor combinations for use in other applications.
            </p>
            <div className="space-y-3">
              <button className="w-full px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 transition-colors">
                Export as SVG
              </button>
              <button className="w-full px-4 py-2 bg-charcoal-600 text-white hover:bg-charcoal-700 transition-colors">
                Export as PNG
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
